/* eslint-disable @typescript-eslint/no-magic-numbers */
import {
  ILiveMapIncidentItem,
  IncidentStatusEnum,
  IRideTableDto,
  nowTDateISO,
  TimeZoneEnum,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import {
  LIVE_MAP_DEBUG_FAKE_OFFSET_HALF_RANGE_DEG,
  LIVE_MAP_DEBUG_INCIDENT_ALTERNATE_COLORS,
  LIVE_MAP_DEBUG_INCIDENT_PROFILES,
  LIVE_MAP_DEBUG_INCIDENT_RIDE_STRIDE,
  liveMapDebugFakeIncidentIdForRide,
} from "./live-map.incidents-layer.constants";
import type { ILiveMapIncidentGeoPosition } from "./live-map.incidents-layer.models";
import { pickFirstStationLatLng } from "./live-map.incidents-layer.utils";

const pseudoRandomUnitPair = (rideId: number): { u: number; v: number } => {
  const x = Math.sin(rideId * 12.9898) * 43758.5453;
  const y = Math.cos(rideId * 78.233) * 43758.5453;

  return {
    u: x - Math.floor(x),
    v: y - Math.floor(y),
  };
};

/**
 * Fake `ILiveMapIncidentItem` per ride for map UI preview (offset from first station, stable per ride id).
 * **Call only when `useDebugMode().debugMode` is true** (`?debugMode` in URL). Do not gate on localhost/staging.
 */
export const buildDebugFakeLiveMapIncidents = (
  mapRides: readonly IRideTableDto[],
  typeName: string,
  timezone: TimeZoneEnum,
): (ILiveMapIncidentItem & Required<ILiveMapIncidentGeoPosition>)[] => {
  const result: (ILiveMapIncidentItem &
    Required<ILiveMapIncidentGeoPosition>)[] = [];

  let profileIndex = 0;

  for (let rideIndex = 0; rideIndex < mapRides.length; rideIndex += 1) {
    if (rideIndex % LIVE_MAP_DEBUG_INCIDENT_RIDE_STRIDE !== 0) {
      continue;
    }

    const ride = mapRides[rideIndex];
    const pos = pickFirstStationLatLng(ride);

    if (!pos) {
      continue;
    }

    const profile =
      LIVE_MAP_DEBUG_INCIDENT_PROFILES[
        profileIndex % LIVE_MAP_DEBUG_INCIDENT_PROFILES.length
      ];
    const useAlternateDebugColor = profileIndex % 2 === 1;
    profileIndex += 1;

    let incidentTypeColor = profile.incidentTypeColor ?? null;

    if (useAlternateDebugColor) {
      if (profile.incidentTypeIconType) {
        incidentTypeColor =
          LIVE_MAP_DEBUG_INCIDENT_ALTERNATE_COLORS[
            profile.incidentTypeIconType
          ] ?? incidentTypeColor;
      } else {
        incidentTypeColor = rydeColors.liveMapDebugIncidentPurple;
      }
    }

    const { u, v } = pseudoRandomUnitPair(ride.id);
    const half = LIVE_MAP_DEBUG_FAKE_OFFSET_HALF_RANGE_DEG;
    const latitude = pos.latitude + (u - 0.5) * 2 * half;
    const longitude = pos.longitude + (v - 0.5) * 2 * half;

    result.push({
      id: liveMapDebugFakeIncidentIdForRide(ride.id),
      rideId: ride.id,
      status: IncidentStatusEnum.New,
      createdAt: nowTDateISO(timezone, false),
      typeName: `${typeName} (${profile.typeLabel})`,
      incidentTypeId: profile.incidentTypeId ?? null,
      incidentTypeIconType: profile.incidentTypeIconType ?? null,
      incidentTypeColor: incidentTypeColor ?? null,
      driverName: "Michael Oren",
      driverPhone: "+1 415 555 0198",
      userCreatedByName: "Sarah Levin",
      creatorPhone: "+1 212 555 0142",
      latitude,
      longitude,
    });
  }

  return result;
};
